import Client from '../../../model/api/Client';
import React, { useCallback, useEffect, useState } from 'react';
import validator from 'validator';
import AppCookies from '../../../model/cookies/AppCookies';

interface IProps {
	modal: any;
	apiClient: Client;
	codexUrl: string;
	termsUrl: string;
	gdprUrl: string;
	defaultEmail?: string;
}

interface IErrors {
	global: string;
	description: string;
	email: string;
	reason: string;
}

interface IValues {
	offerId: number | null;
	description: string;
	email: string;
	reason: string;
}

export const Form = ({ modal, apiClient, termsUrl, gdprUrl, defaultEmail }: IProps): React.ReactNode => {
	const [isDisabled, setIsDisabled] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	// State to hold the selected row ID from the external button
	const [errors, setErrors] = useState<IErrors>({
		global: '',
		description: '',
		email: '',
		reason: ''
	});
	const [formValues, setFormValues] = useState<IValues>({
		offerId: null,
		description: '',
		email: defaultEmail ?? '',
		reason: ''
	});

	const onChange = useCallback(
		(e: any) => {
			const { name, value } = e.target;
			setFormValues({
				...formValues,
				[name]: value
			});
		},
		[setFormValues, formValues]
	);

	const sendForm = useCallback(
		(e: any) => {
			e.preventDefault();

			// basic validation
			let newErrors: IErrors = {
				global: '',
				description: !formValues.description ? 'Popis je povinný' : '',
				email: !formValues.email ? 'Email je povinný' : '',
				reason: !formValues.reason ? 'Důvod nahlášení je povinný' : ''
			};

			// extra validation
			if (newErrors.email === '' && !validator.isEmail(formValues.email)) {
				newErrors.email = 'Email je ve špatném formátu';
			}

			if (newErrors.description === '' && formValues.description.length > 1000) {
				newErrors.description = 'Text důvodu je příliš dlouhý';
			}

			// always refresh errors - empty after submit
			setErrors(newErrors);
			const noErrors = Object.values(newErrors).every((value) => value === '');

			if (noErrors) {
				setIsDisabled(true);

				const onSuccess = (response: any) => {
					if (response.result.success === true) {
						AppCookies.saveUserEmail(formValues.email);
						setIsSubmitted(true);
					}

					if (response.result.success === false) {
						for (const key of Object.keys(response.result.errors)) {
							const error = response.result.errors[key];

							if (error.type === 'email') {
								setErrors({
									...errors,
									email: 'Email je ve špatném formátu'
								});
								return;
							}
							if (error.type === 'duplicate') {
								setErrors({
									...errors,
									global: 'Tento inzerát jste již nahlásili'
								});
								return;
							}
						}
					}
				};

				const onCatch = (error: any) => {
					console.error(error);
					alert('Nastala chyba při odesílání formuláře, zkuste to prosím znovu.');
				};

				const onFinal = () => {
					setIsDisabled(false);
				};

				apiClient.addOfferReport(formValues).then(onSuccess).catch(onCatch).finally(onFinal);
			}
		},
		[apiClient, formValues, errors, setErrors, setIsSubmitted, setIsDisabled]
	);

	const close = useCallback(
		(e: any) => {
			e.preventDefault();
			modal.getInstance(document.getElementById('modalReportPopUp')!).hide();
		},
		[modal]
	);

	const addReportClick = useCallback(
		(event: { target: any }) => {
			const button = event.target;
			const id = parseInt(button.getAttribute('data-id'));

			if (formValues.offerId !== id || isSubmitted) {
				setErrors({
					global: '',
					description: '',
					email: '',
					reason: ''
				});

				setFormValues({
					offerId: id,
					description: '',
					email: defaultEmail ?? '',
					reason: ''
				});
			}

			setIsSubmitted(false);
		},
		[formValues, setErrors, setFormValues, setIsSubmitted, isSubmitted, defaultEmail]
	);

	const createListeners = useCallback(
		(buttons: HTMLCollection) => {
			Array.from(buttons).forEach((button) => {
				button.addEventListener('click', addReportClick);
			});
		},
		[addReportClick]
	);

	const removeListeners = useCallback(
		(buttons: HTMLCollection) => {
			Array.from(buttons).forEach((button) => {
				button.removeEventListener('click', addReportClick);
			});
		},
		[addReportClick]
	);

	useEffect(() => {
		// refresh form when click on report button
		const buttons = document.getElementsByClassName('offer-add-report');
		createListeners(buttons);

		return () => {
			removeListeners(buttons);
		};
	}, [createListeners, removeListeners]);

	if (isSubmitted) {
		return (
			<div className="submitted">
				<div className="alert alert-success" role="alert">
					Děkujeme za nahlášení inzerátu. Váš požadavek bude zpracován.
				</div>
				<div className="mt-4 text-left">
					<button type="submit" className="btn btn-primary btn-medium" onClick={close}>
						Zavřít okno
					</button>
				</div>
			</div>
		);
	} else {
		return (
			<form onSubmit={sendForm}>
				{errors.global && <div className="alert alert-danger">{errors.global}</div>}

				<div className="form-row form-group required">
					<label className="h5 control-label" htmlFor={'report-reason-type'}>
						Důvod nahlášení
					</label>
					<select
						id={'report-reason-type'}
						name="reason"
						className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
						value={formValues.reason}
						onChange={onChange}
						required>
						<option value="">{'Vyberte'}</option>
						<option value="0">Nevhodné / nekorektní informace v inzerátu</option>
						<option value="1">Neaktuální pracovní nabídka</option>
						<option value="2">Podezřelá pracovní nabídka</option>
					</select>
					{errors.reason && <span className="invalid-feedback">{errors.reason}</span>}
				</div>

				<div className="form-row form-group required mt-4">
					<label className="h5 control-label" htmlFor={'report-description'}>
						Popis, proč je inzerát závadný
					</label>
					<textarea
						id={'report-description'}
						name="description"
						className={`form-control ${errors.description ? 'is-invalid' : ''}`}
						autoComplete="off"
						value={formValues.description}
						onChange={onChange}
						placeholder="Popište podrobněji důvod nahlášení"
						maxLength={1000}
					/>
					{errors.description && <span className="invalid-feedback">{errors.description}</span>}
				</div>

				<div className="text-muted mt-2">Počet znaků: {formValues.description.length} / 1000</div>

				<div className="form-row form-group required mt-4">
					<label className="h5 control-label" htmlFor={'report-email'}>
						Vaše e-mailová adresa
					</label>
					<input
						id={'report-email'}
						name="email"
						className={`form-control ${errors.email ? 'is-invalid' : ''}`}
						autoComplete="off"
						placeholder="E-mailová adresa"
						value={formValues.email}
						onChange={onChange}
					/>
					{errors.email && <span className="invalid-feedback">{errors.email}</span>}
				</div>

				<div className="mt-4 text-left">
					<button type="submit" className="btn btn-primary btn-medium" disabled={isDisabled}>
						{isDisabled ? 'Probíhá odesílaní' : 'Nahlásit inzerát'}
					</button>
				</div>

				<div className="mt-5 fs-tiny">
					Pokračováním souhlasíte se{' '}
					<a href={gdprUrl} target={'_blank'} rel="noreferrer">
						Zásady ochrany osobních údajů
					</a>
					{' a '}
					<a href={termsUrl} target={'_blank'} rel="noreferrer">
						Obchodními podmínkami
					</a>{' '}
					pro uživatele webu JenFirmy.cz a JenPráce.cz.
				</div>
			</form>
		);
	}
};
